import { BACKEND_URL } from '../config';
import type { DiffereNavigationRequest } from '../types/app';
import type { LibraryDiffereOriginMetadata, LibraryOriginType } from '../types/library';
import type { LibraryOriginLookupResponse } from '../types/library';
import type { TrainingHistoryContext } from './trainingHistory';
import {
  DIFFERE_SUB_TYPE_LABELS,
  formatDiffereDateRange,
  formatDiffereTypeLabel,
} from './trainingHistory';

type LibraryGameOriginFields = {
  id?: number;
  origin_type?: string;
  origin_id?: string;
  origin_donne_num?: number;
  origin_metadata?: LibraryDiffereOriginMetadata | null;
  note?: string;
  tags?: string[];
  is_partial?: boolean;
  contract?: number;
  game_data?: { contract?: number };
  shared_by?: string;
};

export function isLibraryDiffereOrigin(originType?: string): boolean {
  return originType === 'differe_indiv' || originType === 'differe_tq';
}

export function parseLibraryDiffereMetadata(
  game: LibraryGameOriginFields,
): LibraryDiffereOriginMetadata | undefined {
  const meta = game.origin_metadata;
  if (meta?.differeId != null && meta.playerPseudo) {
    return meta;
  }
  return undefined;
}

export function differeContextFromLibraryGame(
  game: LibraryGameOriginFields,
): TrainingHistoryContext['differe'] | undefined {
  const meta = parseLibraryDiffereMetadata(game);
  if (meta) {
    return {
      differeId: meta.differeId,
      differeType: meta.differeType,
      subType: meta.subType,
      dateStart: meta.dateStart,
      dateEnd: meta.dateEnd,
      pseudo: meta.playerPseudo,
      pseudoDisplay: meta.playerDisplay,
      dealNum: meta.dealNum ?? game.origin_donne_num,
      partieId: meta.partieId,
    };
  }

  if (isLibraryDiffereOrigin(game.origin_type) && game.origin_id && game.origin_donne_num != null) {
    return {
      differeId: Number(game.origin_id),
      differeType: game.origin_type === 'differe_indiv' ? 'indiv' : 'tripl',
      dealNum: game.origin_donne_num,
    };
  }

  return undefined;
}

export function buildLibraryHistoryContext(
  game: LibraryGameOriginFields & { id: number },
  source: 'own' | 'shared',
): TrainingHistoryContext {
  const contract = game.contract ?? game.game_data?.contract;
  const contractLabels: Record<number, string> = {
    0: 'Passe',
    1: 'Petite',
    2: 'Garde',
    3: 'G. Sans',
    4: 'G. Contre',
  };

  const ctx: TrainingHistoryContext = {
    kind: 'library',
    library: {
      gameId: game.id,
      source,
      sharedBy: source === 'shared' ? game.shared_by : undefined,
      originType: game.origin_type,
      originId: game.origin_id,
      originDonneNum: game.origin_donne_num,
      note: game.note,
      contractLabel: contract != null ? (contractLabels[contract] ?? String(contract)) : undefined,
      tags: game.tags,
      isPartial: game.is_partial,
    },
  };

  const differe = differeContextFromLibraryGame(game);
  if (differe) ctx.differe = differe;

  return ctx;
}

export function canOpenDiffereFromLibrary(game: LibraryGameOriginFields): boolean {
  const d = differeContextFromLibraryGame(game);
  if (!d?.differeId || !d.differeType) return false;
  return !!d.pseudo && (d.dealNum != null || d.partieId != null);
}

export function buildDiffereNavigationFromLibrary(
  game: LibraryGameOriginFields,
): DiffereNavigationRequest | null {
  const d = differeContextFromLibraryGame(game);
  if (!canOpenDiffereFromLibrary(game) || !d?.pseudo) return null;
  const diffType = d.differeType as 'indiv' | 'tripl' | 'quad';
  if (diffType !== 'indiv' && diffType !== 'tripl' && diffType !== 'quad') return null;
  return {
    diffType,
    differeId: Number(d.differeId),
    playerPseudo: d.pseudo,
    playerDisplay: d.pseudoDisplay,
    dealNum: d.dealNum,
    partieId: d.partieId,
    view: 'replay',
  };
}

export function formatLibraryDiffereCardTitle(
  game: LibraryGameOriginFields,
  originLabels: Record<LibraryOriginType, string>,
): string {
  const meta = parseLibraryDiffereMetadata(game);
  const differe = differeContextFromLibraryGame(game);
  const originType = game.origin_type as LibraryOriginType | undefined;
  const fallbackOrigin = originType ? (originLabels[originType] ?? originType) : 'Bibliothèque';

  if (!differe) {
    const donneSuffix = game.origin_donne_num ? ` #${game.origin_donne_num}` : '';
    return `${fallbackOrigin}${donneSuffix}`;
  }

  const typeLabel = formatDiffereTypeLabel(differe) ?? fallbackOrigin;
  const dateRange = formatDiffereDateRange(differe.dateStart, differe.dateEnd);
  const player = meta?.playerDisplay ?? differe.pseudoDisplay ?? differe.pseudo ?? '';
  const donne = differe.dealNum != null ? ` · donne ${differe.dealNum}` : '';

  const head = dateRange ? `${typeLabel} ${dateRange}` : typeLabel;
  return player ? `${head} — ${player}${donne}` : `${head}${donne}`;
}

export function buildLibraryDiffereOriginMetadata(input: {
  diffType: 'indiv' | 'tripl' | 'quad';
  differeId: number;
  subType?: string;
  dateStart?: string;
  dateEnd?: string;
  playerPseudo: string;
  playerDisplay?: string;
  dealNum?: number;
  partieId?: number;
}): LibraryDiffereOriginMetadata {
  return {
    differeType: input.diffType,
    differeId: input.differeId,
    subType: input.subType,
    dateStart: input.dateStart,
    dateEnd: input.dateEnd,
    playerPseudo: input.playerPseudo,
    playerDisplay: input.playerDisplay,
    dealNum: input.dealNum,
    partieId: input.partieId,
  };
}

export function libraryDiffereSubTypeLabel(subType?: string): string | undefined {
  if (!subType) return undefined;
  return DIFFERE_SUB_TYPE_LABELS[subType] ?? subType;
}

export async function fetchLibraryGameByOrigin(params: {
  pseudo: string;
  originType: string;
  originId: string;
  originDonneNum?: number;
  partieId?: number;
  playerPseudo?: string;
}): Promise<LibraryOriginLookupResponse> {
  const qs = new URLSearchParams({
    pseudo: params.pseudo,
    origin_type: params.originType,
    origin_id: params.originId,
  });
  if (params.partieId != null) qs.set('partie_id', String(params.partieId));
  if (params.originDonneNum != null) qs.set('origin_donne_num', String(params.originDonneNum));
  if (params.playerPseudo) qs.set('player_pseudo', params.playerPseudo);

  const res = await fetch(`${BACKEND_URL}/api/library/games/by-origin?${qs}`);
  if (!res.ok) {
    return { found: false, game: null };
  }
  return res.json() as Promise<LibraryOriginLookupResponse>;
}
